
// import React, { useState, useEffect } from "react";
// import { useSearchParams } from "react-router-dom"; // react-router v6+
// import "./OverallReport.css";
// import Tabs from "../ReportTab/Tabs";
// import OverviewPage from "./OverviewPage";
// import AssetsDetails from "./AssetsDetails";
// import RuleDetails from "./RuleDetails";
// import AboutReports from "./AboutReports";

// const OverallReport = () => {
//   const [searchParams, setSearchParams] = useSearchParams();

//   // read filters from URL if present, else defaults
//   const [filters, setFilters] = useState({
//     parentDomain: searchParams.get("parentDomain") || "All Parent Domains",
//     domain: searchParams.get("domain") || "All Domains",
//     subDomain: searchParams.get("subDomain") || "All Sub Domains",
//     product: searchParams.get("product") || "All Products",
//   });

//   const [activeTab, setActiveTab] = useState(
//     searchParams.get("activeTab") || "overview"
//   );

//   // whenever filters or activeTab change → update URL
//   useEffect(() => {
//     setSearchParams({
//       activeTab,
//       parentDomain: filters.parentDomain,
//       domain: filters.domain,
//       subDomain: filters.subDomain,
//       product: filters.product,
//     });
//   }, [filters, activeTab, setSearchParams]);

//   return (
//     <div className="dash-wrapper">
//       <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />

//       {activeTab === "overview" && (
//         <OverviewPage filters={filters} setFilters={setFilters} />
//       )}
//       {activeTab === "assets" && (
//         <AssetsDetails filters={filters} setFilters={setFilters} />
//       )}
//       {activeTab === "rules" && (
//         <RuleDetails filters={filters} setFilters={setFilters} />
//       )}
//       {activeTab === "reports" && (
//         <AboutReports filters={filters} setFilters={setFilters} />
//       )}
//     </div>
//   );
// };

// export default OverallReport;


import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import "./OverallReport.css";
import Tabs from "../ReportTab/Tabs";
import Filters from "../ReportTab/Filters";
import OverviewPage from "./OverviewPage";
import AssetsDetails from "./AssetsDetails";
import RuleDetails from "./RuleDetails";
import AboutReports from "./AboutReports";

const DEFAULT_PARENT = "All Parent Domains";
const DEFAULT_DOMAIN = "All Domains";
const DEFAULT_SUB = "All Sub Domains";
const DEFAULT_PRODUCT = "All Products";

const OverallReport = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState({
    parentDomain: searchParams.get("parentDomain") || DEFAULT_PARENT,
    domain: searchParams.get("domain") || DEFAULT_DOMAIN,
    subDomain: searchParams.get("subDomain") || DEFAULT_SUB,
    product: searchParams.get("product") || DEFAULT_PRODUCT,
  });

  const [activeTab, setActiveTab] = useState(
    searchParams.get("activeTab") || "overview"
  );

  // This is the tree we receive from whichever tab fetched it (same shape as res.data.filters.parent_domains)
  const [filterOptionsTree, setFilterOptionsTree] = useState({ parentDomains: [] });

  useEffect(() => {
    setSearchParams({
      activeTab,
      parentDomain: filters.parentDomain,
      domain: filters.domain,
      subDomain: filters.subDomain,
      product: filters.product,
    });
  }, [filters, activeTab, setSearchParams]);

  const handleResetFilters = () => {
  setFilters({
    parentDomain: DEFAULT_PARENT,
    domain: DEFAULT_DOMAIN,
    subDomain: DEFAULT_SUB,
    product: DEFAULT_PRODUCT,
  });
};

  // ---------- helpers to compute filtered lists ----------
  const filteredDomains = useMemo(() => {
    const parents = filterOptionsTree.parentDomains || [];
    if (filters.parentDomain !== DEFAULT_PARENT) {
      const sel = parents.find((pd) => pd.parent_domain === filters.parentDomain);
      return sel ? sel.domains || [] : [];
    }
    return parents.flatMap((pd) => pd.domains || []);
  }, [filterOptionsTree, filters.parentDomain]);

  const filteredSubDomains = useMemo(() => {
    if (filters.domain !== DEFAULT_DOMAIN) {
      const domainObj = filteredDomains.find((d) => d.domain === filters.domain);
      return domainObj ? domainObj.sub_domains || [] : [];
    }
    return filteredDomains.flatMap((d) => d.sub_domains || []);
  }, [filteredDomains, filters.domain]);

  const filteredProducts = useMemo(() => {
    if (filters.subDomain !== DEFAULT_SUB) {
      const sdObj = filteredSubDomains.find((sd) => sd.sub_domain === filters.subDomain);
      return sdObj ? sdObj.products || [] : [];
    }
    return filteredSubDomains.flatMap((sd) => sd.products || []);
  }, [filteredSubDomains, filters.subDomain]);

  // ---------- handlers (same cascade logic you already use) ----------
  const handleParentDomainChange = (e) => {
    const newParent = e.target.value;
    let newDomain = DEFAULT_DOMAIN;
    let newSubDomain = DEFAULT_SUB;
    let newProduct = DEFAULT_PRODUCT;

    if (newParent !== DEFAULT_PARENT) {
      const selectedParent = (filterOptionsTree.parentDomains || []).find(
        (pd) => pd.parent_domain === newParent
      );
      if (selectedParent) {
        const relatedDomains = selectedParent.domains || [];
        if (relatedDomains.length === 1) {
          newDomain = relatedDomains[0].domain;
          const subDomains = relatedDomains[0].sub_domains || [];
          if (subDomains.length === 1) {
            newSubDomain = subDomains[0].sub_domain;
            const products = subDomains[0].products || [];
            if (products.length === 1) newProduct = products[0];
          }
        }
      }
    }

    setFilters({
      parentDomain: newParent,
      domain: newDomain,
      subDomain: newSubDomain,
      product: newProduct,
    });
  };

  const handleDomainChange = (e) => {
    const newDomain = e.target.value;
    let newSubDomain = DEFAULT_SUB;
    let newProduct = DEFAULT_PRODUCT;

    const domainObj = filteredDomains.find((d) => d.domain === newDomain);
    const relatedSubDomains = domainObj ? domainObj.sub_domains : [];

    if (relatedSubDomains.length === 1) {
      newSubDomain = relatedSubDomains[0].sub_domain;
      const products = relatedSubDomains[0].products || [];
      if (products.length === 1) newProduct = products[0];
    }

    setFilters((prev) => ({
      ...prev,
      domain: newDomain,
      subDomain: newSubDomain,
      product: newProduct,
    }));
  };

  const handleSubDomainChange = (e) => {
    const newSubDomain = e.target.value;
    let newProduct = DEFAULT_PRODUCT;

    const subDomainObj = filteredSubDomains.find((sd) => sd.sub_domain === newSubDomain);
    const relatedProducts = subDomainObj ? subDomainObj.products : [];

    if (relatedProducts.length === 1) newProduct = relatedProducts[0];

    setFilters((prev) => ({
      ...prev,
      subDomain: newSubDomain,
      product: newProduct,
    }));
  };

  const handleProductChange = (e) => {
    setFilters((prev) => ({ ...prev, product: e.target.value }));
  };

  // ---------- called by tabs when they fetch filters (parent_domains) ----------
  const handleFilterOptionsFromTab = (parentDomainsArray = []) => {
    setFilterOptionsTree({ parentDomains: parentDomainsArray });

    // apply safe auto-select if current selections are invalid or can be auto-selected
    setFilters((prev) => {
      const parents = parentDomainsArray || [];

      // ensure parent exists
      const parentNames = parents.map((p) => p.parent_domain);
      let newParent = parentNames.includes(prev.parentDomain) ? prev.parentDomain : DEFAULT_PARENT;

      // domains for newParent
      const domainsForParent =
        newParent === DEFAULT_PARENT ? parents.flatMap((p) => p.domains || []) : (parents.find((p) => p.parent_domain === newParent)?.domains || []);

      // domain validation / auto-select
      const domainNames = domainsForParent.map((d) => d.domain);
      let newDomain = domainNames.includes(prev.domain) ? prev.domain : DEFAULT_DOMAIN;
      if (newDomain === DEFAULT_DOMAIN && domainNames.length === 1) newDomain = domainNames[0];

      // subdomains
      const subDomainsForDomain =
        newDomain === DEFAULT_DOMAIN ? domainsForParent.flatMap((d) => d.sub_domains || []) : (domainsForParent.find((d) => d.domain === newDomain)?.sub_domains || []);

      const subNames = subDomainsForDomain.map((sd) => sd.sub_domain);
      let newSubDomain = subNames.includes(prev.subDomain) ? prev.subDomain : DEFAULT_SUB;
      if (newSubDomain === DEFAULT_SUB && subNames.length === 1) newSubDomain = subNames[0];

      // products
      const productsForSub =
        newSubDomain === DEFAULT_SUB ? subDomainsForDomain.flatMap((sd) => sd.products || []) : (subDomainsForDomain.find((sd) => sd.sub_domain === newSubDomain)?.products || []);

      const prodNames = productsForSub || [];
      let newProduct = prodNames.includes(prev.product) ? prev.product : DEFAULT_PRODUCT;
      if (newProduct === DEFAULT_PRODUCT && prodNames.length === 1) newProduct = prodNames[0];

      return {
        parentDomain: newParent,
        domain: newDomain,
        subDomain: newSubDomain,
        product: newProduct,
      };
    });
  };

  return (
   <div className="dash-wrapper flex flex-col dashboard-dashboard poppins-regular">
  {/* Row with filters on left and tabs on right */}
  <div className="flex justify-between items-start gap-6 pt-4 pl-4 pr-4 pb-1">
    <div>
      <Filters
        filterOptions={{
          parentDomains: filterOptionsTree.parentDomains,
          domains: filteredDomains,
          subDomains: filteredSubDomains,
          products: filteredProducts,
        }}
        parentDomain={filters.parentDomain}
        domain={filters.domain}
        subDomain={filters.subDomain}
        product={filters.product}
        onParentDomainChange={handleParentDomainChange}
        onDomainChange={handleDomainChange}
        onSubDomainChange={handleSubDomainChange}
        onProductChange={handleProductChange}
         onReset={handleResetFilters} 
      />
    </div>

    <div >
      <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  </div>

  {/* Content below filters + tabs row */}
  <div>
    {activeTab === "overview" && (
      <OverviewPage
        filters={filters}
        setFilters={setFilters}
        onFilterOptionsChange={handleFilterOptionsFromTab}
      />
    )}
    {activeTab === "assets" && (
      <AssetsDetails
        filters={filters}
        setFilters={setFilters}
        onFilterOptionsChange={handleFilterOptionsFromTab}
      />
    )}
    {activeTab === "rules" && (
      <RuleDetails
        filters={filters}
        setFilters={setFilters}
        onFilterOptionsChange={handleFilterOptionsFromTab}
      />
    )}
  </div>
</div>

  );
};

export default OverallReport;
